import moment from 'moment';
import { defineRule, configure } from 'vee-validate';
import { localize } from '@vee-validate/i18n';
// eslint-disable-next-line camelcase
import { email, min, regex, required, alpha_num } from '@vee-validate/rules';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import {
  type IRule,
  RuleType,
  ValidationMode,
  type ValidationConfig,
  type InputValidator,
  type InputProps,
} from './types';

function birthDateValidator(value: number | string, { locale, dateFormatString }: any) {
  moment.locale(locale);

  // handle special output cases
  if (['undefined', 'invalid'].includes(value as string)) {
    return false;
  }

  // when work with timestamp - apply null as a Format String.
  const appliedDateFormatString = typeof value === 'string' ? dateFormatString : null;

  if (value && !Number.isNaN(value)) {
    return moment.utc(value, appliedDateFormatString).isBefore(moment.utc());
  }

  return true;
}
function dateNotEmptyValidator(value: any) {
  return value !== 'invalid';
}
function valueNotUndefinedValidator(value: any) {
  return value !== undefined;
}
function phoneNumberValidator(phone: string) {
  return !phone || isValidPhoneNumber(phone);
}

// coped from https://www.npmjs.com/package/israeli-id-validator
function israeliIdValidator(israeliId = '') {
  let strId = String(israeliId).trim();

  if (strId.length !== 9 || +strId <= 0) {
    return false;
  }
  if (strId.length < 9) {
    while (strId.length < 9) {
      strId = `0${strId}`;
    }
  }
  let counter = 0;
  let rawVal;
  let actualVal;

  for (let i = 0; i < strId.length; i += 1) {
    rawVal = Number(strId[i]) * ((i % 2) + 1);
    actualVal = rawVal > 9 ? (rawVal - 9) : rawVal;
    counter += actualVal;
  }

  return (counter % 10 === 0);
}

/**
 * Validate value does not match another field's value
 *
 * @example config:
 * "validator": {
 *                 "name": "register.partnersPhone",
 *                 "rules": {
 *                   "required": true,
 *                   "phone": true,
 *                   "doesNotMatchField": "@register.phone"
 *                 }
 *               }
 * Note @<validator.name> returns value of the target field
 *
 * @param value
 * @param params
 */
function doesNotMatchFieldValidator(value: any, params: any) {
  return value === undefined || value !== params[0];
}

/**
 * Validate field pairs are unique
 *
 * Config: an array of field names to compare pair by pair, e.g. ["@register.email", "@register.partnersEmail", "@register.phone", "@register.partnersPhone"]
 * Return true (valid) if at least one pair does not match or the value is undefined.
 *
 * @example config:
 * "validator": {
 *                 "name": "register.partnersPhone",
 *                 "rules": {
 *                   "required": true,
 *                   "phone": true,
 *                   "fieldPairsAreUniqueValidator": ["@register.email", "@register.partnersEmail", "@register.phone", "@register.partnersPhone"]
 *                 }
 *               }
 * Note @<validator.name> returns value of the target field
 *
 * @param value
 * @param params
 */
function fieldPairsAreUniqueValidator(value: any, params: any) {
  const pairsCount = params.length / 2;
  let matchingPairsCount = 0;

  // panic if params length is not even
  if (params.length % 2 !== 0) {
    throw new Error('FieldPairsAreUniqueValidator: params length must be even');
  }

  // compare params in pairs
  for (let i = 0; i < params.length; i += 2) {
    if (params[i] !== undefined && params[i] === params[i + 1]) {
      matchingPairsCount += 1;
    }
  }

  // if at least one pair is unique, input is valid
  return matchingPairsCount < pairsCount;
}

// @ts-ignore
const ValidatorImplementation: IRule = {
  [RuleType.REGEX]: regex,
  // eslint-disable-next-line camelcase
  [RuleType.ALPHA_NUMERIC]: alpha_num,
  [RuleType.REQUIRED]: required,
  [RuleType.EMAIL]: email,
  [RuleType.MIN]: min,
  [RuleType.BIRTH_DATE]: birthDateValidator,
  [RuleType.DATE_NOT_EMPTY]: dateNotEmptyValidator,
  [RuleType.DATE_NOT_UNDEFINED]: valueNotUndefinedValidator,
  [RuleType.PHONE]: phoneNumberValidator,
  [RuleType.ISRAELI_ID]: israeliIdValidator,
  [RuleType.DOES_NOT_MATCH_FIELD]: doesNotMatchFieldValidator,
  [RuleType.FIELD_PAIRS_ARE_UNIQUE]: fieldPairsAreUniqueValidator,
};

// TODO get translations before init validation provider (looks like it not required, remove it after verification v7)
/*
const fetchValidationMessages = async (
  localeLang = 'en',
  basePath = '/ui-assets/access/configurations',
) => HttpClientFactory.baseClient
  .get(`${basePath}/global/translations/global.${localeLang}.json`)
  .then((result: AxiosResponse<any>) => {
    if (result && result.data && result.data.validations) {
      return result.data.validations;
    }

    // fallback (optional, most likely never hit)
    return {};
  });
*/

/**
 * Transform vee-validate v3 to v4 format
 *
 * Preserves backward compatibility with the old ui-assets translations.
 *
 * @param errorMessages
 */
const upgradeErrorMessages = (errorMessages: any = {}): any => Object.entries(errorMessages)
  .reduce(
    (errorMessagesV4, [key, val]: any) => {
      errorMessagesV4[key] = val.message;
      errorMessagesV4[key] = errorMessagesV4[key]?.replace(/{_([^}]*)_}/g, '{$1}');

      return errorMessagesV4;
    },
    {},
  );

const init = async (language = 'en', errorMessages: any = {}, validationMessagesOverrides = {}) => {
  const errorMessagesV4 = upgradeErrorMessages(errorMessages);

  errorMessages.required = errorMessages.required?.message || {};

  // define global validation rules
  Object.keys(ValidatorImplementation).forEach((key: string) => {
    defineRule(key, ValidatorImplementation[key]);
  });

  // define error messages
  configure({
    generateMessage: localize(language, {
      messages: errorMessagesV4,
      fields: validationMessagesOverrides,
    }),
  });
};

export {
  init,
  IRule,
  RuleType,
  ValidationMode,
  ValidationConfig,
  InputValidator,
  InputProps,
  phoneNumberValidator,
};
